import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { RiLockPasswordLine, RiLogoutBoxRLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/primitives/avatar';
import { Button } from '@/components/primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/primitives/dropdown-menu';
import { clearJwtToken } from './jwt-manager';
import { useUser } from './index';

type ClerkWindow = Window & {
  Clerk?: Record<string, unknown> & { loggedIn?: boolean };
};

function getUserInitials(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function UserButton() {
  const { user } = useUser() as {
    user: { firstName?: string; lastName?: string; emailAddresses?: { emailAddress: string }[] } | undefined;
  };
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!user) return null;

  const primaryEmail = user.emailAddresses?.[0]?.emailAddress?.trim() || '';
  const fullName = [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(' ');
  const displayName = fullName || primaryEmail || 'User';
  const displayEmail = primaryEmail || 'No email';

  const handleLogout = () => {
    clearJwtToken();

    if (typeof window !== 'undefined') {
      const clerkWindow = window as ClerkWindow;
      clerkWindow.Clerk = { ...(clerkWindow.Clerk || {}), loggedIn: false };
    }

    queryClient.clear();
    void navigate('/auth/sign-in');
  };

  return (
    <div className="shrink-0">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={buttonRef}
            variant="secondary"
            size="sm"
            className="h-6 w-6 rounded-full bg-white p-0 hover:bg-gray-50 focus:outline-hidden focus:ring-0 focus-visible:shadow-none"
          >
            <Avatar className="h-6 w-6 border border-gray-200">
              <AvatarImage src={`${window.location.origin}/images/avatar.svg`} alt={displayName} />
              <AvatarFallback className="bg-primary-base text-static-white text-xs">
                {getUserInitials(displayName)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[300px] border border-gray-200 bg-white shadow-sm" sideOffset={5}>
          <div className="flex items-center gap-3 p-3">
            <Avatar className="h-8 w-8 border border-gray-200">
              <AvatarImage src={`${window.location.origin}/images/avatar.svg`} alt={displayName} />
              <AvatarFallback className="bg-primary-base text-static-white text-sm">
                {getUserInitials(displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
              <p className="truncate text-xs text-gray-600">{displayEmail}</p>
            </div>
          </div>
          <DropdownMenuSeparator />
          {/* Account (change password). Without this the self-hosted profile was unreachable. */}
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setIsOpen(false);
              void navigate('/settings/account');
            }}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <RiLockPasswordLine className="h-3.5 w-3.5 shrink-0 text-gray-500" />
              <span>Account &amp; password</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 text-gray-700 hover:bg-gray-50"
            onClick={handleLogout}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <RiLogoutBoxRLine className="h-3.5 w-3.5 shrink-0 text-gray-500" />
              <span>Logout</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
